import React, { useEffect, useRef, useState } from 'react';
import { Button, Alert, Typography, Spin, Divider } from 'antd';
import { LockOutlined, GoogleOutlined, MailOutlined, SwapOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/api';
import './LoginPage.css';

const ERROR_HINTS: Record<string, string> = {
  not_authorized: 'このアカウントには Analytics ダッシュボードへのアクセス権限がありません。運営/管理者アカウントでログインしてください。',
  token_invalid: 'Auth0 トークンの検証に失敗しました。再度ログインしてください。',
  callback_error: 'Auth0 コールバック処理中にエラーが発生しました。時間をおいて再試行してください。',
  session_expired: 'セッションの有効期限が切れました。再ログインしてください。',
};

const LoginPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading, getIdTokenClaims, user, logout } = useAuth0();
  const [verifying, setVerifying] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverErrorEmail, setServerErrorEmail] = useState<string | null>(null);
  const startedRef = useRef(false);

  const queryError = params.get('error');

  // Auth0 redirect 戻り → backend 検証 → /insights (ref ガードで 1 回だけ実行、
  // verifying state を deps にしない: re-render の cleanup で cancelled が早期 true 化する罠回避)
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (startedRef.current) return;
    startedRef.current = true;
    setVerifying(true);
    (async () => {
      try {
        const claims = await getIdTokenClaims();
        const idToken = claims?.__raw;
        if (!idToken) {
          setServerError('token_invalid');
          setVerifying(false);
          return;
        }
        const resp = await apiClient.post('/api/auth/verify', { id_token: idToken }) as unknown as { ok?: boolean };
        const ok = resp?.ok === true || (resp as any)?.data?.ok === true;
        if (ok) {
          const next = params.get('next') || '/insights';
          window.location.replace(next);
          return;
        }
        setServerError('callback_error');
        setVerifying(false);
      } catch (e: any) {
        const errCode = e?.response?.data?.error || 'callback_error';
        setServerError(errCode);
        setServerErrorEmail(user?.email || null);
        try { await logout({ logoutParams: { returnTo: window.location.origin + '/login' }, openUrl: false } as any); } catch {}
        setVerifying(false);
      }
    })();
  }, [isLoading, isAuthenticated, getIdTokenClaims, params, user, logout]);

  const handleLoginGoogle = () => {
    setServerError(null);
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/login`,
        connection: 'google-oauth2',
      },
    });
  };

  const handleLoginPassword = () => {
    setServerError(null);
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/login`,
        connection: 'Username-Password-Authentication',
      },
    });
  };

  const handleSwitchAccount = async () => {
    setServerError(null);
    // 先 logout (Auth0 セッション + Google federated) → 再 loginWithRedirect prompt=select_account
    try {
      await logout({
        logoutParams: { returnTo: `${window.location.origin}/login`, federated: true } as any,
        openUrl: async (url: string) => {
          // federated logout 完了後、自動で account picker 付き再 login
          window.location.href = url;
        },
      } as any);
    } catch {
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/login`,
          prompt: 'select_account',
        } as any,
      });
    }
  };

  const displayedError = serverError || queryError;

  if (isLoading || (isAuthenticated && verifying && !displayedError)) {
    return (
      <div className="login-shell">
        <div className="login-card" style={{ textAlign: 'center', padding: '60px 36px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: 'var(--v2-muted)' }}>認証情報を確認中…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo">D</div>
          <div>
            <strong>DIPCeph</strong>
            <span>Operations Analytics</span>
          </div>
        </div>

        <Typography.Title level={3} className="login-title">
          管理者ログイン
        </Typography.Title>
        <Typography.Paragraph className="login-sub">
          このダッシュボードは <strong>運営チーム / 管理者アカウント</strong> 限定です。Auth0 経由でログインしてください。
        </Typography.Paragraph>

        {displayedError && (
          <Alert
            type={displayedError === 'not_authorized' ? 'error' : 'warning'}
            showIcon
            message={displayedError === 'not_authorized' ? 'アクセス拒否' : 'ログインエラー'}
            description={
              <>
                <div>{ERROR_HINTS[displayedError] || `不明なエラー: ${displayedError}`}</div>
                {serverErrorEmail && <div className="login-error-meta">アカウント: {serverErrorEmail}</div>}
              </>
            }
            style={{ marginBottom: 20 }}
          />
        )}

        <Button
          type="primary"
          size="large"
          block
          icon={<GoogleOutlined />}
          onClick={handleLoginGoogle}
          className="login-button login-button-google"
          loading={isLoading}
        >
          Google アカウントでログイン
        </Button>

        <Button
          size="large"
          block
          icon={<MailOutlined />}
          onClick={handleLoginPassword}
          className="login-button login-button-password"
          loading={isLoading}
          style={{ marginTop: 10 }}
        >
          メールアドレスでログイン
        </Button>

        <Divider plain style={{ margin: '18px 0 8px', fontSize: 12 }}>
          <span className="login-divider-text">他環境からの切り替え</span>
        </Divider>

        <Button
          type="text"
          size="small"
          block
          icon={<SwapOutlined />}
          onClick={handleSwitchAccount}
          className="login-switch-link"
        >
          別のアカウントに切り替え (現在のセッションをログアウト)
        </Button>

        <div className="login-footer">
          <LockOutlined /> アクセス権限: <strong>運営チーム</strong> または <strong>管理者アカウント</strong> のみ
          <br />
          問い合わせ: <a href="mailto:r.tou@dentalbrain.co.jp">r.tou@dentalbrain.co.jp</a>
        </div>
      </div>

      <div className="login-bg-orbs" aria-hidden="true">
        <span className="orb orb-1" />
        <span className="orb orb-2" />
        <span className="orb orb-3" />
      </div>
    </div>
  );
};

export default LoginPage;
